import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock3, MessageCircle, Send } from "lucide-react";
import { Reveal, SectionHeading, waLink, PHONE_DISPLAY, EMAIL, ADDRESS, HOURS } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-void border border-slate-700 rounded-xl px-4 py-3.5 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-brand-lime transition-colors";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", device: "Windows Laptop", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received — opening WhatsApp to reach AFZ instantly.");
    } catch {
      toast.info("Opening WhatsApp — the fastest way to reach AFZ.");
    }
    window.open(
      waLink(`Hi AFZ Tech Solution, I'm ${form.name}. Device: ${form.device}. Issue: ${form.message}${form.phone ? ` (Call me: ${form.phone})` : ""}`),
      "_blank"
    );
    setForm({ name: "", phone: "", device: "Windows Laptop", message: "" });
    setSending(false);
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 sm:py-32 bg-void overflow-hidden">
      <div className="absolute -bottom-24 left-1/4 w-[30rem] h-[30rem] rounded-full bg-brand-lime/8 blur-[150px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeading
          eyebrow="Kembangan Workshop Command Center"
          title="Have a computer problem"
          accent="right now?"
          sub="Send the device model and a short description of the issue. Include a photo of any error message if it helps."
        />
        <div className="mt-14 grid lg:grid-cols-2 gap-10">
          <Reveal>
            <div className="h-full bg-card/80 border border-slate-800 rounded-2xl p-8 clip-corner flex flex-col gap-7">
              <div className="flex items-start gap-4">
                <MapPin className="w-6 h-6 text-brand-lime shrink-0 mt-1" />
                <div>
                  <h3 className="font-display text-xl font-bold uppercase text-white">Workshop</h3>
                  <p className="text-slate-400 text-sm mt-1">{ADDRESS}</p>
                  <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-brand-cyan mt-1">2 mins from Kembangan MRT (EW6)</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Clock3 className="w-6 h-6 text-brand-lime shrink-0 mt-1" />
                <div>
                  <h3 className="font-display text-xl font-bold uppercase text-white">Hours</h3>
                  <p className="text-slate-400 text-sm mt-1">{HOURS}</p>
                  <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-brand-cyan mt-1">Quick diagnostic & drop-off</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="w-6 h-6 text-brand-lime shrink-0 mt-1" />
                <div>
                  <h3 className="font-display text-xl font-bold uppercase text-white">Direct Line</h3>
                  <a data-testid="contact-phone-link" href={`tel:${PHONE_DISPLAY.replace(/\s/g, "")}`} className="text-slate-400 text-sm mt-1 block hover:text-brand-lime transition-colors">{PHONE_DISPLAY}</a>
                  <a data-testid="contact-email-link" href={`mailto:${EMAIL}`} className="text-slate-400 text-sm mt-1 flex items-center gap-2 hover:text-brand-lime transition-colors">
                    <Mail className="w-3.5 h-3.5" /> {EMAIL}
                  </a>
                </div>
              </div>
              <a
                data-testid="contact-whatsapp-button"
                href={waLink("Hello AFZ Tech Solution, I need help with a device repair.")}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-auto inline-flex items-center justify-center gap-3 bg-brand-lime text-void font-display font-bold uppercase text-lg px-8 py-4 rounded-full hover:bg-brand-limedark hover:scale-[1.02] transition-all duration-300 shadow-[0_0_30px_rgba(170,255,0,0.4)]"
              >
                <MessageCircle className="w-5 h-5" /> Start on WhatsApp
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <form onSubmit={submit} data-testid="contact-form" className="bg-card/80 border border-slate-800 rounded-2xl p-8 clip-corner space-y-5">
              <h3 className="font-display text-2xl font-bold uppercase text-white">Quick Inquiry</h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <input
                  data-testid="contact-form-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                  className={inputCls}
                />
                <input
                  data-testid="contact-form-phone-input"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="Phone (optional)"
                  className={inputCls}
                />
              </div>
              <select
                data-testid="contact-form-device-select"
                value={form.device}
                onChange={set("device")}
                className={`${inputCls} appearance-none cursor-pointer`}
              >
                {["Windows Laptop", "MacBook / iMac", "Desktop PC", "Office / Business IT", "Other"].map((d) => (
                  <option key={d}>{d}</option>
                ))}
              </select>
              <textarea
                data-testid="contact-form-message-input"
                required
                value={form.message}
                onChange={set("message")}
                placeholder="Describe the issue — e.g. laptop won't power on, Mac stuck on loading bar..."
                rows={4}
                className={`${inputCls} resize-none`}
              />
              <button
                data-testid="contact-form-submit-button"
                type="submit"
                disabled={sending}
                className="w-full inline-flex items-center justify-center gap-3 border border-brand-lime text-brand-lime font-display font-bold uppercase text-lg px-8 py-4 rounded-full hover:bg-brand-lime hover:text-void transition-all duration-300 disabled:opacity-50"
              >
                <Send className="w-5 h-5" /> {sending ? "Sending..." : "Send & Open WhatsApp"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
